import { JUMP_BUFFER_TIME } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioFeedbackPlayer';
import { AudioHighlightChip } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioHighlightChip';
import {
  AudioHighlightMetadata,
  AudioHighlightMetadataType,
} from '@modules/feedback/components/AudioType/types';
import {
  getCurrentPlayingHighlight,
  getLabelFromHighlight,
  getNextHighlightToPlay,
  getPrevHighlightToPlay,
  trackAudioFeedbackEvents,
} from '@modules/feedback/components/AudioType/utils';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FeedbackLabel } from 'v2/core/entities/feedback';
import { UUID } from 'v2/core/entities/uuid';

interface AudioBookmarkControllerProps {
  highlightMetadata: AudioHighlightMetadata[];
  seek: number;
  onJumpToBookmark: (time: number, shouldBuffer: boolean) => void;
  selectedLabels: FeedbackLabel[];
  feedbackID: UUID;
  isAudioPlaying: boolean;
  isDisabled?: boolean;
  isFeedbackCardView: boolean;
}

const AudioBookmarkController = ({
  highlightMetadata: highlights,
  seek,
  onJumpToBookmark,
  selectedLabels,
  feedbackID,
  isAudioPlaying,
  isDisabled,
  isFeedbackCardView,
}: AudioBookmarkControllerProps) => {
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const highlightMetadata = isFeedbackCardView
    ? highlights
    : highlights.filter((highlight) =>
        selectedLabels.some((label) => label.id === highlight.labelID),
      );

  const currentPlayingHighlight = useMemo(
    () => getCurrentPlayingHighlight(highlightMetadata, seek, JUMP_BUFFER_TIME),
    [highlightMetadata, seek],
  );

  const selectedLabel = getLabelFromHighlight(
    selectedLabels,
    currentPlayingHighlight,
  );

  if (highlightMetadata.length === 0) {
    return null;
  }

  const hasNoPrevOccurrence =
    highlightMetadata.length === 0 ||
    seek - 1 <= highlightMetadata[0].startTime;

  const hasNoNextOccurrence =
    highlightMetadata.length === 0 ||
    seek + 1 + JUMP_BUFFER_TIME >=
      highlightMetadata[highlightMetadata.length - 1].startTime;

  const handlePrevOccurrence = () => {
    const prevHighlight = getPrevHighlightToPlay(highlightMetadata, seek, 1);

    if (!prevHighlight) {
      return;
    }

    trackAudioFeedbackEvents({
      eventName: 'Audio Feedback - Highlights Bar: Prev Button Clicked',
      feedbackID,
    });

    onJumpToBookmark(
      prevHighlight.startTime,
      prevHighlight.type !== AudioHighlightMetadataType.KeywordSummary,
    );
  };

  const handleNextOccurrence = () => {
    const nextHighlight = getNextHighlightToPlay(
      highlightMetadata,
      seek,
      1 + JUMP_BUFFER_TIME,
    );

    if (!nextHighlight) {
      return;
    }

    trackAudioFeedbackEvents({
      eventName: 'Audio Feedback - Highlights Bar: Next Button Clicked',
      feedbackID,
      triggerLocation: isFeedbackCardView
        ? 'Feedback List Card'
        : 'Feedback Detail Card',
    });

    onJumpToBookmark(
      nextHighlight.startTime,
      nextHighlight.type !== AudioHighlightMetadataType.KeywordSummary,
    );
  };

  return (
    <>
      <Divider sx={{ borderColor: 'divider' }} />
      <Box
        sx={{
          px: 3,
          py: 2,
          borderRadius: 2,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          boxShadow: 'none',
          backgroundColor: 'feedback.audioPlayer',
          backgroundImage: 'none',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Highlights:
            </Typography>
            {isFeedbackRedesignEnabled ? (
              <>
                <Button
                  color="primary"
                  size="small"
                  disabled={isDisabled || hasNoPrevOccurrence}
                  onClick={handlePrevOccurrence}
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <ChevronLeftRounded />
                </Button>
                <Button
                  color="primary"
                  size="small"
                  disabled={isDisabled || hasNoNextOccurrence}
                  onClick={handleNextOccurrence}
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <ChevronRightRounded />
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  startIcon={<ChevronLeftRounded />}
                  disabled={isDisabled || hasNoPrevOccurrence}
                  onClick={handlePrevOccurrence}
                  variant="outlined"
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  endIcon={<ChevronRightRounded />}
                  disabled={isDisabled || hasNoNextOccurrence}
                  onClick={handleNextOccurrence}
                  variant="outlined"
                >
                  Next
                </Button>
              </>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={
              currentPlayingHighlight ? 'space-between' : 'flex-end'
            }
            spacing={1}
          >
            {currentPlayingHighlight ? (
              <Stack
                direction="row"
                spacing={2}
                marginLeft="auto"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  color={
                    currentPlayingHighlight ? 'text.secondary' : 'text.disabled'
                  }
                >
                  Playing:
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AudioHighlightChip
                    shouldAnimate={isAudioPlaying}
                    shouldShowSummaryIcon
                    highlight={currentPlayingHighlight}
                    maxWidth={200}
                    shouldShowLabelTooltip
                    label={selectedLabel}
                  />
                </Stack>
              </Stack>
            ) : (
              <Typography variant="subtitle2" color="text.secondary">
                No Highlight Playing
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default AudioBookmarkController;
